import { NODE_TYPES } from '@flow/nodes';

// Validation types
export const VALIDATION_TYPES = {
  ERROR: 'error',
  WARNING: 'warning',
};

/**
 * Check if step_id is not empty
 * @param {string} stepId - The step_id to validate
 * @returns {Object|null} Validation error or null if valid
 */
export function validateStepIdNotEmpty(stepId) {
  if (!stepId || stepId.trim() === '') {
    return {
      type: VALIDATION_TYPES.ERROR,
      field: 'step_id',
      message: 'Step ID is required and cannot be empty',
      code: 'EMPTY_STEP_ID',
    };
  }

  return null;
}

/**
 * Check if step_type is not empty
 * @param {string} stepType - The step_type to validate
 * @returns {Object|null} Validation error or null if valid
 */
export function validateStepTypeNotEmpty(stepType) {
  if (stepType === undefined || stepType === null || stepType.trim() === '') {
    return {
      type: VALIDATION_TYPES.ERROR,
      field: 'step_type',
      message: 'Step Type is required and cannot be empty',
      code: 'EMPTY_STEP_TYPE',
    };
  }

  return null;
}

/**
 * Check if step_id is unique across all plan step nodes
 * @param {string} stepId - The step_id to validate
 * @param {string} currentNodeId - The ID of the node being validated
 * @param {Array} allNodes - All nodes in the flow
 * @returns {Object|null} Validation error or null if valid
 */
export function validateStepIdUnique(stepId, currentNodeId, allNodes) {
  if (!stepId || stepId.trim() === '') {
    return null; // Empty handled by different validator
  }

  const planStepNodes = allNodes.filter((node) => node.type === NODE_TYPES.PlanStepNode && node.id !== currentNodeId);

  const duplicateNode = planStepNodes.find((node) => node.data.planStepData?.step_id === stepId);

  if (duplicateNode) {
    return {
      type: VALIDATION_TYPES.ERROR,
      field: 'step_id',
      message: `Step ID "${stepId}" is already used by another step`,
      code: 'DUPLICATE_STEP_ID',
      conflictingNodeId: duplicateNode.id,
    };
  }

  return null;
}

/**
 * Extract step_id references from plan step data
 * Searches through:
 * - query_list: ${step_id} placeholders
 * - preprocess: step_ids arrays and settings_of_step_ids
 * - conditional_next_step_ids: Yes/No arrays
 *
 * @param {Object} planStepData - The plan step configuration
 * @returns {Array} Array of {stepId, field, context} objects
 */
export function extractStepIdReferences(planStepData) {
  const references = [];

  // 1. Extract from query_list - find ${step_id} patterns
  if (planStepData.query_list && Array.isArray(planStepData.query_list)) {
    planStepData.query_list.forEach((queryItem, queryIndex) => {
      if (typeof queryItem === 'string') {
        // Find ${step_id} patterns
        const matches = queryItem.matchAll(/\$\{([^}]+)\}/g);
        for (const match of matches) {
          references.push({
            stepId: match[1],
            field: 'query_list',
            context: `query #${queryIndex + 1}`,
          });
        }
      } else if (queryItem && typeof queryItem === 'object' && queryItem.query) {
        const matches = queryItem.query.matchAll(/\$\{([^}]+)\}/g);
        for (const match of matches) {
          references.push({
            stepId: match[1],
            field: 'query_list',
            context: `query #${queryIndex + 1}`,
          });
        }
      }
    });
  }

  // 2. Extract from preprocess functions
  if (planStepData.preprocess && Array.isArray(planStepData.preprocess)) {
    planStepData.preprocess.forEach((preprocessItem, ppIndex) => {
      // step_ids array
      if (preprocessItem.step_ids && Array.isArray(preprocessItem.step_ids)) {
        preprocessItem.step_ids.forEach((stepId) => {
          if (stepId && stepId.trim() !== '') {
            references.push({
              stepId: stepId,
              field: 'preprocess',
              context: `function #${ppIndex + 1} step_ids`,
            });
          }
        });
      }

      // settings_of_step_ids array
      if (preprocessItem.settings_of_step_ids && Array.isArray(preprocessItem.settings_of_step_ids)) {
        preprocessItem.settings_of_step_ids.forEach((settingItem, settingIndex) => {
          if (settingItem.step_id) {
            references.push({
              stepId: settingItem.step_id,
              field: 'preprocess',
              context: `function #${ppIndex + 1} settings_of_step_ids #${settingIndex + 1}`,
            });
          }
        });
      }
    });
  }

  // 3. Extract from conditional_next_step_ids
  if (planStepData.conditional_next_step_ids) {
    const conditional = planStepData.conditional_next_step_ids;

    if (conditional.Yes && Array.isArray(conditional.Yes)) {
      conditional.Yes.forEach((stepId) => {
        if (stepId && stepId.trim() !== '') {
          references.push({
            stepId: stepId,
            field: 'conditional_next_step_ids',
            context: 'Yes branch',
          });
        }
      });
    }

    if (conditional.No && Array.isArray(conditional.No)) {
      conditional.No.forEach((stepId) => {
        if (stepId && stepId.trim() !== '') {
          references.push({
            stepId: stepId,
            field: 'conditional_next_step_ids',
            context: 'No branch',
          });
        }
      });
    }
  }

  return references;
}

/**
 * Find all step_id references in a plan step and validate they exist
 * @param {string} nodeId - The node being validated
 * @param {Object} planStepData - The plan step configuration data
 * @param {Array} allNodes - All nodes in the flow
 * @returns {Array} Array of validation errors (empty if all valid)
 */
export function validateStepIdReferences(nodeId, planStepData, allNodes) {
  const validations = [];

  // Get all valid step_ids from all plan step nodes
  const allStepIds = allNodes
    .filter((node) => node.type === NODE_TYPES.PlanStepNode)
    .map((node) => node.data.planStepData?.step_id)
    .filter((id) => id && id.trim() !== '');

  // Find references in different fields
  const referencedStepIds = extractStepIdReferences(planStepData);

  // Check each reference
  referencedStepIds.forEach(({ stepId, field, context }) => {
    if (!allStepIds.includes(stepId)) {
      validations.push({
        type: VALIDATION_TYPES.ERROR,
        field: field,
        message: `Referenced step ID "${stepId}" does not exist${context ? ` (in ${context})` : ''}`,
        code: 'INVALID_STEP_ID_REFERENCE',
        referencedStepId: stepId,
      });
    }
  });

  return validations;
}

/**
 * Validate a single plan step node
 * @param {string} nodeId - Node being validated
 * @param {Object} planStepData - Plan step configuration
 * @param {Array} allNodes - All nodes in the flow
 * @returns {Object} Validation result
 */
export function validatePlanStepNode(nodeId, planStepData, allNodes) {
  const validations = [];

  const stepId = planStepData.step_id;
  const stepType = planStepData.step_type;

  // 1. Validate step_id not empty
  const emptyResult = validateStepIdNotEmpty(stepId);
  if (emptyResult) {
    validations.push(emptyResult);
  }

  // 2. Validate step_id is unique (only if not empty)
  if (stepId && stepId.trim() !== '') {
    const uniqueResult = validateStepIdUnique(stepId, nodeId, allNodes);
    if (uniqueResult) {
      validations.push(uniqueResult);
    }
  }

  // 3. Validate step_type not empty
  const emptyStepTypeResult = validateStepTypeNotEmpty(stepType);
  if (emptyStepTypeResult) {
    validations.push(emptyStepTypeResult);
  }

  // 4. Validate step_id references
  const referenceResults = validateStepIdReferences(nodeId, planStepData, allNodes);
  if (referenceResults && referenceResults.length > 0) {
    validations.push(...referenceResults);
  }

  return {
    nodeId,
    stepId: stepId,
    validations,
  };
}

/**
 * Validate all plan step nodes in the flow
 * @param {Array} allNodes - All nodes in the flow
 * @returns {Object} Map of nodeId to validation results
 */
export function validateAllPlanStepNodes(allNodes) {
  const allValidations = {};
  const planStepNodes = allNodes.filter((n) => n.type === NODE_TYPES.PlanStepNode);

  planStepNodes.forEach((node) => {
    const result = validatePlanStepNode(node.id, node.data.planStepData || {}, allNodes);
    allValidations[node.id] = result;
  });

  return allValidations;
}
